import { LitElement, html, nothing } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { t } from '../i18n';
import { api, apiGetDedupe } from '../api';
import { showToast } from '../toast';
import { defineZenComponent } from './register';
import './gmail-sync-filter-editor';
import './gcal-sync-filter-editor';
import {
  calendarModesForAccount,
  gmailFilterDraft,
  normalizeEmailAddresses,
  type CalendarMode,
  type GmailFilterDraft,
  type ProviderDiscovery,
} from '../connection-sync-rules-logic';
import {
  cleanExpandedConnectionIds,
  disconnectFocusTarget,
  groupMyConnections,
  myConnections,
  presentMyConnectionRow,
  visibleConnectProviders,
  type MyConnectionConnectProvider,
  type MyConnectionAccount,
  type MyConnectionFocusTarget,
  type MyConnectionGroup,
  type MyConnectionIntegrationsPolicy,
  type MyConnectionReadOnlyReason,
  type MyConnectionRow,
  type MyConnectionVisibility,
} from '../my-connections-logic';

class ZenMyConnectionsSection extends LitElement {
  static properties = {
    accounts: { attribute: false },
    viewerUserId: { attribute: false },
    integrationsPolicy: { attribute: false },
    providersConfigured: { attribute: false },
    loading: { attribute: false },
    connectingProvider: { attribute: false },
    busyId: { attribute: false },
    editingId: { attribute: false },
    confirmingId: { attribute: false },
    purgeChoice: { attribute: false },
    expandedIds: { attribute: false },
    profileSyncRules: { attribute: false },
    discoveries: { attribute: false },
    gmailDrafts: { attribute: false },
    calendarDrafts: { attribute: false },
    savingFilterId: { attribute: false },
  };

  declare accounts: MyConnectionAccount[];
  declare viewerUserId: string;
  declare integrationsPolicy: MyConnectionIntegrationsPolicy;
  declare providersConfigured: readonly string[];
  declare loading: boolean;
  declare connectingProvider: MyConnectionConnectProvider['id'] | '';
  declare busyId: string;
  declare editingId: string;
  declare confirmingId: string;
  declare purgeChoice: boolean;
  declare expandedIds: Set<string>;
  declare profileSyncRules: Record<string, unknown>;
  declare discoveries: Map<string, ProviderDiscovery>;
  declare gmailDrafts: Map<string, GmailFilterDraft>;
  declare calendarDrafts: Map<string, Record<string, { mode: CalendarMode }>>;
  declare savingFilterId: string;

  private disconnectButtonRefs = new Map<string, HTMLButtonElement>();
  private connectButtonRefs = new Map<MyConnectionConnectProvider['id'], HTMLButtonElement>();
  private discoveryEpoch = 0;

  constructor() {
    super();
    this.accounts = [];
    this.viewerUserId = '';
    this.integrationsPolicy = {};
    this.providersConfigured = [];
    this.loading = false;
    this.connectingProvider = '';
    this.busyId = '';
    this.editingId = '';
    this.confirmingId = '';
    this.purgeChoice = false;
    this.expandedIds = new Set();
    this.profileSyncRules = {};
    this.discoveries = new Map();
    this.gmailDrafts = new Map();
    this.calendarDrafts = new Map();
    this.savingFilterId = '';
  }

  createRenderRoot() {
    return this;
  }

  private _onLangChange = () => this.requestUpdate();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('zen-lang-change', this._onLangChange);
    void this.load();
  }

  disconnectedCallback() {
    this.discoveryEpoch += 1;
    document.removeEventListener('zen-lang-change', this._onLangChange);
    super.disconnectedCallback();
  }

  private visibleAccounts(): MyConnectionAccount[] {
    return myConnections(this.accounts, this.viewerUserId);
  }

  private mutationBusy(): boolean {
    return Boolean(this.connectingProvider || this.busyId || this.editingId || this.savingFilterId);
  }

  private async load(fresh = false) {
    this.discoveryEpoch += 1;
    this.loading = true;
    this.requestUpdate();
    try {
      // viewerUserId は引き続き GET /api/profile から解決する。
      // 連携ポリシーは connected-accounts の sanitized read projection から
      // 読む。workspace admin route の rollout gate とは独立した個人接続契約。
      const getter = fresh ? api : apiGetDedupe;
      const [profileData, accountsData] = await Promise.all([
        getter('/api/profile') as Promise<any>,
        getter('/api/connected-accounts') as Promise<any>,
      ]);
      this.viewerUserId = profileData?.profile?.user_id || '';
      this.profileSyncRules = profileData?.profile?.sync_rules || {};
      this.integrationsPolicy = (accountsData?.integrations_policy as MyConnectionIntegrationsPolicy) ?? {};
      this.accounts = accountsData?.accounts || [];
      this.providersConfigured = Array.isArray(accountsData?.providers_configured)
        ? accountsData.providers_configured
        : [];
      this.discoveries = new Map();
      this.expandedIds = cleanExpandedConnectionIds(
        this.expandedIds,
        this.visibleAccounts().map((account) => account.id),
      );
      for (const account of this.visibleAccounts()) {
        if (!this.expandedIds.has(account.id)) continue;
        const row = presentMyConnectionRow(account, this.integrationsPolicy);
        if ((account.provider === 'gmail' || account.provider === 'google_calendar') && row.access.mode === 'editable') {
          this.prepareFilterDraft(account);
          void this.discoverProviderSettings(account);
        }
      }
    } catch {
      showToast(t('myConnectionsLoadError') as string, 'error');
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  private async connectAccount(provider: MyConnectionConnectProvider) {
    if (this.mutationBusy()) return;
    this.connectingProvider = provider.id;
    this.requestUpdate();
    try {
      const data = await api(provider.endpoint, { method: 'POST', json: {} });
      if (typeof data.auth_url !== 'string' || !data.auth_url) {
        throw new Error('Missing OAuth URL');
      }
      window.location.href = data.auth_url;
    } catch {
      showToast(t('myConnectionsConnectError') as string, 'error');
      this.connectingProvider = '';
      this.requestUpdate();
    }
  }

  private toggleDetails(accountId: string) {
    const next = new Set(this.expandedIds);
    if (next.has(accountId)) next.delete(accountId);
    else {
      next.add(accountId);
      const account = this.accounts.find((item) => item.id === accountId);
      if (account && (account.provider === 'gmail' || account.provider === 'google_calendar')) {
        const row = presentMyConnectionRow(account, this.integrationsPolicy);
        this.prepareFilterDraft(account);
        if (row.access.mode === 'editable') void this.discoverProviderSettings(account);
      }
    }
    this.expandedIds = next;
  }

  private prepareFilterDraft(account: MyConnectionAccount) {
    if (account.provider === 'gmail' && !this.gmailDrafts.has(account.id)) {
      this.gmailDrafts = new Map(this.gmailDrafts).set(account.id, gmailFilterDraft(this.profileSyncRules, account));
    }
    if (account.provider === 'google_calendar' && !this.calendarDrafts.has(account.id)) {
      this.calendarDrafts = new Map(this.calendarDrafts).set(account.id, calendarModesForAccount(account));
    }
  }

  private async discoverProviderSettings(account: MyConnectionAccount) {
    if (this.discoveries.has(account.id)) return;
    this.discoveries = new Map(this.discoveries).set(account.id, { status: 'loading', items: [] });
    const epoch = this.discoveryEpoch;
    try {
      const response = await api(`/api/connected-accounts/${account.id}/sync-rules/discovery`);
      if (epoch !== this.discoveryEpoch || !this.accounts.some((item) => item.id === account.id)) return;
      const discovery: ProviderDiscovery = response?.status === 'available'
        ? { status: 'available', items: Array.isArray(response.items) ? response.items : [] }
        : { status: 'degraded', reason: 'provider_unavailable', items: [] };
      this.discoveries = new Map(this.discoveries).set(account.id, discovery);
    } catch {
      if (epoch !== this.discoveryEpoch || !this.accounts.some((item) => item.id === account.id)) return;
      this.discoveries = new Map(this.discoveries).set(account.id, {
        status: 'degraded', reason: 'provider_unavailable', items: [],
      });
    }
  }

  private updateGmailDraft(accountId: string, draft: GmailFilterDraft) {
    this.gmailDrafts = new Map(this.gmailDrafts).set(accountId, draft);
  }

  private updateCalendarDraft(accountId: string, modes: Record<string, { mode: CalendarMode }>) {
    this.calendarDrafts = new Map(this.calendarDrafts).set(accountId, modes);
  }

  private async saveGmailFilter(
    account: MyConnectionAccount,
    draft: GmailFilterDraft,
    dirty: { global: boolean; account: boolean },
  ) {
    if (this.discoveries.get(account.id)?.status !== 'available' || this.mutationBusy() || (!dirty.global && !dirty.account)) return;
    this.savingFilterId = account.id;
    try {
      const normalized: GmailFilterDraft = {
        ...draft,
        exclude_senders: normalizeEmailAddresses(draft.exclude_senders),
        exclude_recipients: normalizeEmailAddresses(draft.exclude_recipients),
      };
      if (dirty.global || dirty.account) {
        await api('/api/connected-accounts');
        await api(`/api/connected-accounts/${account.id}/sync-rules`, {
          method: 'PATCH', json: { gmail: normalized },
        });
      }
      showToast(t('connectionFilterSaveSuccess') as string, 'success');
      this.gmailDrafts = new Map();
      await this.load(true);
    } catch {
      showToast(t('connectionFilterSaveError') as string, 'error');
      this.gmailDrafts = new Map();
      await this.load(true);
    } finally {
      this.savingFilterId = '';
    }
  }

  private async saveCalendarFilter(account: MyConnectionAccount, modes: Record<string, { mode: CalendarMode }>) {
    if (this.discoveries.get(account.id)?.status !== 'available' || this.mutationBusy()) return;
    this.savingFilterId = account.id;
    try {
      // Fresh account readback is intentionally performed immediately before
      // the server-side fresh-row merge/CAS write.
      const accountsData = await api('/api/connected-accounts');
      if (!(accountsData?.accounts || []).some((item: MyConnectionAccount) => item.id === account.id)) {
        throw new Error('account not found');
      }
      await api(`/api/connected-accounts/${account.id}/sync-rules`, {
        method: 'PATCH', json: { calendar: { calendars: modes } },
      });
      showToast(t('connectionFilterSaveSuccess') as string, 'success');
      this.calendarDrafts = new Map();
      await this.load(true);
    } catch {
      showToast(t('connectionFilterSaveError') as string, 'error');
      this.calendarDrafts = new Map();
      await this.load(true);
    } finally {
      this.savingFilterId = '';
    }
  }

  private openConfirm(accountId: string) {
    this.confirmingId = accountId;
    this.purgeChoice = false;
  }

  private async closeConfirm(accountId: string) {
    this.confirmingId = '';
    this.purgeChoice = false;
    await this.updateComplete;
    this.disconnectButtonRefs.get(accountId)?.focus();
  }

  private focusAfterDisconnect(target: MyConnectionFocusTarget) {
    if (target.type === 'disconnect') {
      this.disconnectButtonRefs.get(target.accountId)?.focus();
      return;
    }
    const firstProvider = visibleConnectProviders(this.providersConfigured)[0];
    if (firstProvider) this.connectButtonRefs.get(firstProvider.id)?.focus();
  }

  private async disconnect(accountId: string, mode: 'disconnect' | 'purge_revoked' = 'disconnect') {
    if (this.mutationBusy()) return;
    const previousOrder = groupMyConnections(this.visibleAccounts()).map((group) => group.actionAccountId);
    const focusTarget = disconnectFocusTarget(previousOrder, accountId);
    const purge = mode === 'purge_revoked' || this.purgeChoice;
    this.busyId = accountId;
    try {
      await api(`/api/connected-accounts/${accountId}${purge ? '?purge=1' : ''}`, { method: 'DELETE' });
      showToast(t(mode === 'purge_revoked' ? 'myConnectionsPurgeRevokedSuccess' : 'myConnectionsDisconnectSuccess') as string, 'success');
      this.confirmingId = '';
      this.purgeChoice = false;
      await this.load();
      await this.updateComplete;
      this.focusAfterDisconnect(focusTarget);
    } catch {
      showToast(t(mode === 'purge_revoked' ? 'myConnectionsPurgeRevokedError' : 'myConnectionsDisconnectError') as string, 'error');
    } finally {
      this.busyId = '';
      this.requestUpdate();
    }
  }

  private async toggleIngest(account: MyConnectionAccount, nextEnabled: boolean) {
    if (this.mutationBusy()) return;
    const previous = this.accounts;
    // optimistic: 即 UI 更新してから API を呼ぶ
    this.accounts = this.accounts.map((item) =>
      item.id === account.id ? { ...item, ingest_policy: { ...item.ingest_policy, enabled: nextEnabled } } : item,
    );
    this.editingId = account.id;
    try {
      await api(`/api/connected-accounts/${account.id}/ingest-policy`, {
        method: 'PATCH',
        json: { enabled: nextEnabled },
      });
      showToast(
        t(nextEnabled ? 'myConnectionsIngestOnSuccess' : 'myConnectionsIngestOffSuccess') as string,
        'success',
      );
    } catch {
      this.accounts = previous;
      showToast(t('myConnectionsIngestUpdateError') as string, 'error');
    } finally {
      this.editingId = '';
      this.requestUpdate();
    }
  }

  private async changeVisibility(account: MyConnectionAccount, nextVisibility: MyConnectionVisibility) {
    if (this.mutationBusy()) return;
    const previous = this.accounts;
    this.accounts = this.accounts.map((item) =>
      item.id === account.id ? { ...item, visibility: nextVisibility } : item,
    );
    this.editingId = account.id;
    try {
      await api(`/api/connected-accounts/${account.id}/visibility`, {
        method: 'PATCH',
        json: { visibility: nextVisibility },
      });
      showToast(t('myConnectionsVisibilitySuccess') as string, 'success');
    } catch {
      this.accounts = previous;
      showToast(t('myConnectionsVisibilityError') as string, 'error');
    } finally {
      this.editingId = '';
      this.requestUpdate();
    }
  }

  private rememberDisconnectButton(accountId: string, element?: Element) {
    if (element instanceof HTMLButtonElement) this.disconnectButtonRefs.set(accountId, element);
    else this.disconnectButtonRefs.delete(accountId);
  }

  private rememberConnectButton(providerId: MyConnectionConnectProvider['id'], element?: Element) {
    if (element instanceof HTMLButtonElement) this.connectButtonRefs.set(providerId, element);
    else this.connectButtonRefs.delete(providerId);
  }

  private renderPurgeRevokedConfirm(accountId: string) {
    return html`
      <div class="my-connections-confirm">
        <div class="my-connections-confirm-msg">${t('myConnectionsPurgeRevokedConfirmMessage')}</div>
        <div class="my-connections-confirm-actions">
          <button type="button" class="btn btn-sm" @click=${() => this.closeConfirm(accountId)}>
            ${t('myConnectionsConfirmCancel')}
          </button>
          <button
            type="button"
            class="btn btn-sm btn-danger"
            ?disabled=${this.mutationBusy()}
            @click=${() => this.disconnect(accountId, 'purge_revoked')}
          >
            <span class="mi mi-xs" aria-hidden="true">delete</span>${t('myConnectionsPurgeRevokedConfirmOk')}
          </button>
        </div>
      </div>
    `;
  }

  private renderConfirm(accountId: string, kind: MyConnectionGroup['kind']) {
    return html`
      <div class="my-connections-confirm">
        <div class="my-connections-confirm-msg">
          ${t(kind === 'google' ? 'myConnectionsConfirmGoogleMessage' : 'myConnectionsConfirmMessage')}
        </div>
        <label class="my-connections-confirm-purge">
          <input
            type="checkbox"
            .checked=${this.purgeChoice}
            @change=${(event: Event) => { this.purgeChoice = (event.target as HTMLInputElement).checked; }}
          >
          <span>
            ${t('myConnectionsConfirmPurgeLabel')}
            <small class="field-hint">${t('myConnectionsConfirmPurgeHint')}</small>
          </span>
        </label>
        <div class="my-connections-confirm-actions">
          <button type="button" class="btn btn-sm" @click=${() => this.closeConfirm(accountId)}>
            ${t('myConnectionsConfirmCancel')}
          </button>
          <button
            type="button"
            class="btn btn-sm btn-danger"
            ?disabled=${this.mutationBusy()}
            @click=${() => this.disconnect(accountId)}
          >
            <span class="mi mi-xs" aria-hidden="true">link_off</span>${t('myConnectionsConfirmOk')}
          </button>
        </div>
      </div>
    `;
  }

  private readOnlyMessageKey(reason: MyConnectionReadOnlyReason): string {
    if (reason === 'revoked') return 'connectionRowRevokedDescription';
    if (reason === 'error') return 'connectionRowErrorDescription';
    return 'connectionRowIntegrationNotAllowedDescription';
  }

  private renderSummaryReadback(row: MyConnectionRow) {
    const scopeValueKey = row.shareScope.value === 'private'
      ? 'connectionRowVisibilityPrivate'
      : 'connectionRowVisibilityShared';
    return html`
      <span>${t(row.ingestReadbackKey)}</span>
      <span aria-hidden="true">・</span>
      <span>${t('connectionRowShareScopeReadback', { scope: t(scopeValueKey) })}</span>
    `;
  }

  private renderProviderFilterSlot(account: MyConnectionAccount, row: MyConnectionRow) {
    if (row.provider === 'slack' || row.filterLanding.mode !== 'static_unavailable') return nothing;

    const headingId = `my-connection-${row.id}-filter-heading`;
    const discovery = this.discoveries.get(account.id) ?? { status: 'idle', items: [] };
    const readOnly = row.access.mode === 'read_only';
    const gmailDraftValue = this.gmailDrafts.get(account.id) ?? gmailFilterDraft(this.profileSyncRules, account);
    const gmailBaseline = gmailFilterDraft(this.profileSyncRules, account);
    const gmailGlobalDirty = JSON.stringify({
      include_categories: gmailDraftValue.include_categories,
      exclude_senders: normalizeEmailAddresses(gmailDraftValue.exclude_senders),
      exclude_recipients: normalizeEmailAddresses(gmailDraftValue.exclude_recipients),
    }) !== JSON.stringify({
      include_categories: gmailBaseline.include_categories,
      exclude_senders: gmailBaseline.exclude_senders,
      exclude_recipients: gmailBaseline.exclude_recipients,
    });
    const gmailAccountDirty = JSON.stringify(gmailDraftValue.exclude_labels) !== JSON.stringify(gmailBaseline.exclude_labels);
    const calendarDraftValue = this.calendarDrafts.get(account.id) ?? calendarModesForAccount(account);
    const calendarBaseline = calendarModesForAccount(account);
    return html`
      <section class="my-connections-filter-slot" aria-labelledby=${headingId}>
        <h4 id=${headingId} class="my-connections-filter-heading">
          ${t(row.filterLanding.headingKey)}
        </h4>
        ${row.provider === 'gmail'
          ? html`<zen-gmail-sync-filter-editor
              .draft=${gmailDraftValue}
              .discovery=${discovery}
              .disabled=${readOnly}
              .saving=${this.mutationBusy()}
              .globalDirty=${gmailGlobalDirty}
              .accountDirty=${gmailAccountDirty}
              @filter-change=${(event: CustomEvent<GmailFilterDraft>) => this.updateGmailDraft(account.id, event.detail)}
              @filter-save=${(event: CustomEvent<GmailFilterDraft>) => this.saveGmailFilter(account, event.detail, {
                global: gmailGlobalDirty,
                account: gmailAccountDirty,
              })}
            ></zen-gmail-sync-filter-editor>`
          : html`<zen-gcal-sync-filter-editor
              .modes=${calendarDraftValue}
              .discovery=${discovery}
              .disabled=${readOnly}
              .saving=${this.mutationBusy()}
              .dirty=${JSON.stringify(calendarDraftValue) !== JSON.stringify(calendarBaseline)}
              @filter-change=${(event: CustomEvent<Record<string, { mode: CalendarMode }>>) => this.updateCalendarDraft(account.id, event.detail)}
              @filter-save=${(event: CustomEvent<Record<string, { mode: CalendarMode }>>) => this.saveCalendarFilter(account, event.detail)}
            ></zen-gcal-sync-filter-editor>`}
      </section>
    `;
  }

  private renderDetail(account: MyConnectionAccount, row: MyConnectionRow) {
    if (row.access.mode === 'read_only') {
      return html`
        <div class="my-connections-read-only-note">
          <span class="mi mi-sm" aria-hidden="true">info</span>
          <span>${t(this.readOnlyMessageKey(row.access.reason))}</span>
        </div>
        ${this.renderProviderFilterSlot(account, row)}
      `;
    }

    return html`
      <p class="my-connections-detail-description">${t(row.detailDescriptionKey)}</p>
      <div class="my-connections-detail-controls">
        <label class="my-connections-control">
          <span class="my-connections-control-label">${t(row.ingestLabelKey)}</span>
          <button
            type="button"
            class="switch"
            role="switch"
            aria-checked=${row.ingestEnabled}
            aria-label=${t('connectionRowIngestAriaLabel', { provider: row.providerLabel })}
            ?disabled=${this.mutationBusy()}
            @click=${() => this.toggleIngest(account, !row.ingestEnabled)}
          >
            <span class="switch-thumb"></span>
          </button>
        </label>
        ${row.capability === 'ingest_and_share_scope'
          ? html`
              <label class="my-connections-control">
                <span class="my-connections-control-label">${t('connectionRowVisibilityLabel')}</span>
                <select
                  class="my-connections-share-scope"
                  ?disabled=${this.mutationBusy()}
                  .value=${row.shareScope.value}
                  @change=${(event: Event) => this.changeVisibility(
                    account,
                    (event.target as HTMLSelectElement).value as MyConnectionVisibility,
                  )}
                >
                  <option value="private" ?selected=${row.shareScope.value === 'private'}>
                    ${t('connectionRowVisibilityPrivate')}
                  </option>
                  <option value="shared" ?selected=${row.shareScope.value === 'shared'}>
                    ${t('connectionRowVisibilityShared')}
                  </option>
                </select>
              </label>
            `
          : html`
              <div class="my-connections-fixed-scope">
                <span class="my-connections-control-label">${t('connectionRowVisibilityLabel')}</span>
                <span><span class="mi mi-xs" aria-hidden="true">lock</span>${t('connectionRowVisibilityPrivate')}</span>
              </div>
            `}
      </div>
      ${this.renderProviderFilterSlot(account, row)}
    `;
  }

  /** actions を渡すと、グループの見出しを持たない1行の接続（Slack）として、解除を行の見出しに置く。 */
  private renderRow(account: MyConnectionAccount, actions: unknown = nothing) {
    const row = presentMyConnectionRow(account, this.integrationsPolicy);
    const expanded = this.expandedIds.has(account.id);
    const summaryId = `my-connection-${account.id}-summary`;
    const detailId = `my-connection-${account.id}-detail`;
    return html`
      <article class="my-connections-row">
        <div class=${`my-connections-row-header${actions === nothing ? ' my-connections-row-header--nested' : ''}`}>
          <div class="my-connections-provider-mark"><span class="mi" aria-hidden="true">${row.icon}</span></div>
          <button
            type="button"
            id=${summaryId}
            class="my-connections-disclosure"
            aria-expanded=${expanded}
            aria-controls=${detailId}
            @click=${() => this.toggleDetails(account.id)}
          >
            <span class="my-connections-summary-main">
              <span class="my-connections-provider-name">${row.providerLabel}</span>
              <span class="my-connections-account-id" title=${row.providerAccountId}>${row.accountLabel}</span>
              <span class=${`my-connections-status ${row.statusTone}`}>
                <span class="mi mi-xs" aria-hidden="true">${row.statusIcon}</span>
                <span>${t(row.statusLabelKey)}</span>
                <span aria-hidden="true">・</span>
                <span>${t('myConnectionsConnectedAt', { date: new Date(account.created_at).toLocaleDateString() })}</span>
              </span>
              <span class="my-connections-readback">${this.renderSummaryReadback(row)}</span>
              <span class="my-connections-visually-hidden">${t('myConnectionsDisclosureHint')}</span>
            </span>
            <span class="mi my-connections-chevron" aria-hidden="true">
              ${expanded ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          ${actions}
        </div>
        <div
          id=${detailId}
          class="my-connections-detail"
          role="region"
          aria-labelledby=${summaryId}
          ?hidden=${!expanded}
        >
          ${this.renderDetail(account, row)}
        </div>
      </article>
    `;
  }

  private connectProviderFor(kind: MyConnectionGroup['kind']): MyConnectionConnectProvider | undefined {
    return visibleConnectProviders(this.providersConfigured).find((provider) => provider.id === kind);
  }

  private renderGroupActions(group: MyConnectionGroup) {
    if (group.status === 'revoked') {
      const reconnect = this.connectProviderFor(group.kind);
      return html`
        <div class="my-connections-group-actions">
          <button
            ${ref((element) => this.rememberDisconnectButton(group.actionAccountId, element))}
            type="button"
            class="btn btn-sm"
            ?disabled=${this.mutationBusy()}
            @click=${() => this.openConfirm(group.actionAccountId)}
          >
            <span class="mi mi-xs" aria-hidden="true">delete</span>${t('myConnectionsPurgeRevokedAction')}
          </button>
          ${reconnect
            ? html`<button
                type="button"
                class="btn btn-sm"
                ?disabled=${this.mutationBusy()}
                @click=${() => this.connectAccount(reconnect)}
              >
                <span class="mi mi-xs" aria-hidden="true">add_link</span>${t('myConnectionsReconnectAction')}
              </button>`
            : nothing}
        </div>
      `;
    }
    return html`
      <button
        ${ref((element) => this.rememberDisconnectButton(group.actionAccountId, element))}
        type="button"
        class="btn btn-sm my-connections-disconnect-action"
        ?disabled=${this.mutationBusy()}
        @click=${() => this.openConfirm(group.actionAccountId)}
      >
        <span class="mi mi-xs" aria-hidden="true">link_off</span>${t('myConnectionsDisconnectAction')}
      </button>
    `;
  }

  private renderGroup(group: MyConnectionGroup) {
    const revoked = group.status === 'revoked';
    const confirming = this.confirmingId === group.actionAccountId;
    if (group.kind === 'slack' && !revoked) {
      return html`
        <section class="my-connections-group">
          ${this.renderRow(group.accounts[0], this.renderGroupActions(group))}
          ${confirming ? this.renderConfirm(group.actionAccountId, group.kind) : nothing}
        </section>
      `;
    }
    return html`
      <section class=${`my-connections-group${revoked ? ' revoked' : ''}`}>
        <div class="my-connections-row-header">
          <div class="my-connections-provider-mark">
            <span class="mi" aria-hidden="true">${group.kind === 'google' ? 'account_circle' : 'hub'}</span>
          </div>
          <div class="my-connections-summary-main">
            <span class="my-connections-provider-name">
              ${group.kind === 'google' ? t('myConnectionsGroupGoogle') : 'Slack'}
            </span>
            <span class="my-connections-account-id" title=${group.providerAccountId}>${group.accountLabel}</span>
            ${revoked
              ? html`<span class="my-connections-status muted">
                  <span class="mi mi-xs" aria-hidden="true">link_off</span>
                  <span>${t('myConnectionsStatusRevoked')}</span>
                </span>`
              : group.kind === 'google'
                ? html`<span class="my-connections-readback">${t('myConnectionsGroupGoogleNote')}</span>`
                : nothing}
          </div>
          ${this.renderGroupActions(group)}
        </div>
        ${confirming
          ? revoked
            ? this.renderPurgeRevokedConfirm(group.actionAccountId)
            : this.renderConfirm(group.actionAccountId, group.kind)
          : nothing}
        ${revoked
          ? nothing
          : html`<div class="my-connections-group-rows">${group.accounts.map((account) => this.renderRow(account))}</div>`}
      </section>
    `;
  }

  render() {
    const rows = this.visibleAccounts();
    const groups = groupMyConnections(rows);
    const hasGoogle = rows.some((account) => account.status === 'active'
      && (account.provider === 'gmail' || account.provider === 'google_calendar'));
    const hasSlack = rows.some((account) => account.status === 'active' && account.provider === 'slack');
    return html`
      <div class="group-note">
        <span class="mi" aria-hidden="true">info</span>
        <span>${t('myConnectionsNote')}</span>
      </div>
      ${this.loading
        ? html`<div class="settings-section-placeholder">${t('myConnectionsLoading')}</div>`
        : rows.length === 0
          ? html`<div class="settings-section-placeholder">${t('myConnectionsEmpty')}</div>`
          : html`<div class="my-connections-list">${groups.map((group) => this.renderGroup(group))}</div>`}
      <div class="my-connections-actions">
        ${visibleConnectProviders(this.providersConfigured).map((provider) => html`
          <button
            ${ref((element) => this.rememberConnectButton(provider.id, element))}
            type="button"
            class="btn btn-primary"
            ?disabled=${this.mutationBusy() || (provider.id === 'google' ? hasGoogle : hasSlack)}
            @click=${() => this.connectAccount(provider)}
          >
            <span class="mi mi-sm" aria-hidden="true">${provider.icon}</span>${t(provider.labelKey)}
          </button>
        `)}
        <span class="my-connections-hint">${t('myConnectionsPurgeHint')}</span>
      </div>
    `;
  }
}

defineZenComponent('zen-my-connections-section', ZenMyConnectionsSection);
